import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  FileText, Search, ExternalLink, Shield, BookOpen, AlertTriangle,
  List, ClipboardList, Globe, Building2, Filter, CheckCircle2, Clock, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconForCategoria = (icono) => {
  const m = { shield: Shield, book: BookOpen, 'alert-triangle': AlertTriangle, 'file-text': FileText, list: List, clipboard: ClipboardList };
  return m[icono] || FileText;
};

const estadoBadge = (estado) => {
  if (estado === 'vigente') return { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-700', Icon: CheckCircle2 };
  if (estado === 'por_vencer') return { label: 'Por Vencer', cls: 'bg-amber-100 text-amber-700', Icon: Clock };
  if (estado === 'vencido') return { label: 'Vencido', cls: 'bg-red-100 text-red-700', Icon: XCircle };
  return null;
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d + 'T12:00:00').toLocaleDateString('es-CL'); } catch { return d; }
};

export const PortalRepositorio = () => {
  const { user } = useAuth();
  const [categorias, setCategorias] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catFilter, setCatFilter] = useState('all');
  const [gerenciaFilter, setGerenciaFilter] = useState(null); // resolved in useEffect
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [metaRes, docsRes, gersRes] = await Promise.all([
        axios.get(`${API}/trabajador/repositorio`),
        axios.get(`${API}/repositorio/documentos`),
        axios.get(`${API}/gerencias`),
      ]);
      setCategorias(metaRes.data?.categorias || []);
      setDocumentos(docsRes.data || []);
      setGerencias(gersRes.data || []);
      if (gerenciaFilter === null) {
        setGerenciaFilter(metaRes.data?.gerencia_id_trabajador || 'all');
      }
    } catch {
      toast.error('Error al cargar documentos');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocs = useMemo(() => {
    const s = search.trim().toLowerCase();
    return documentos.filter(d => {
      if (catFilter !== 'all' && d.categoria_id !== catFilter) return false;
      // Gerencia filter: show transversales always unless a specific gerencia is picked
      if (d.categoria_tipo === 'gerencia') {
        if (gerenciaFilter && gerenciaFilter !== 'all' && d.gerencia_id !== gerenciaFilter) return false;
      }
      if (estadoFilter !== 'all' && d.estado_vigencia !== estadoFilter) return false;
      if (s) {
        const hay = [d.nombre, d.codigo, d.descripcion, d.categoria_nombre, d.gerencia_nombre].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [documentos, catFilter, gerenciaFilter, estadoFilter, search]);

  useEffect(() => { setPage(1); }, [catFilter, gerenciaFilter, estadoFilter, search]);
  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE));
  const pagedDocs = filteredDocs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const countByCat = useMemo(() => {
    const m = {};
    documentos.forEach(d => {
      if (d.categoria_tipo === 'gerencia' && gerenciaFilter && gerenciaFilter !== 'all' && d.gerencia_id !== gerenciaFilter) return;
      m[d.categoria_id] = (m[d.categoria_id] || 0) + 1;
    });
    return m;
  }, [documentos, gerenciaFilter]);

  const totalDocs = useMemo(() => {
    return documentos.filter(d => {
      if (d.categoria_tipo === 'gerencia' && gerenciaFilter && gerenciaFilter !== 'all' && d.gerencia_id !== gerenciaFilter) return false;
      return true;
    }).length;
  }, [documentos, gerenciaFilter]);

  const gerenciaNombre = (id) => gerencias.find(g => g.id === id)?.nombre || id;

  const transversales = categorias.filter(c => c.tipo === 'transversal');
  const porGerencia = categorias.filter(c => c.tipo === 'gerencia');

  return (
    <div className="p-4 max-w-[1300px] mx-auto" data-testid="portal-repositorio">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-900">Control Documental</h1>
        <p className="text-xs text-slate-500">
          Consulta documentos transversales y de gerencia
          {user?.gerencia_nombre && <> · Tu gerencia: <strong>{user.gerencia_nombre}</strong></>}
        </p>
      </div>

      {/* Categorias */}
      <div className="space-y-3 mb-4">
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Globe className="w-3 h-3" /> Transversales</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <MiniCat selected={catFilter === 'all'} onClick={() => setCatFilter('all')} nombre="Todos" icon={FileText} color="#64748b" count={totalDocs} />
            {transversales.map(c => (
              <MiniCat key={c.id} selected={catFilter === c.id} onClick={() => setCatFilter(c.id)}
                nombre={c.nombre} icon={iconForCategoria(c.icono)} color="#0891b2" count={countByCat[c.id] || 0} />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Por Gerencia</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {porGerencia.map(c => (
              <MiniCat key={c.id} selected={catFilter === c.id} onClick={() => setCatFilter(c.id)}
                nombre={c.nombre} icon={iconForCategoria(c.icono)} color="#ea580c" count={countByCat[c.id] || 0} />
            ))}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card className="border-0 shadow-sm mb-3">
        <CardContent className="p-2.5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
            <div className="md:col-span-6 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input placeholder="Buscar por nombre, codigo, descripcion..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="portal-repo-search" />
            </div>
            <div className="md:col-span-3">
              <Select value={gerenciaFilter || 'all'} onValueChange={setGerenciaFilter}>
                <SelectTrigger className="h-9"><Building2 className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las gerencias</SelectItem>
                  {gerencias.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="h-9"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="vigente">Vigente</SelectItem>
                  <SelectItem value="por_vencer">Por Vencer</SelectItem>
                  <SelectItem value="vencido">Vencido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-700">{filteredDocs.length} documento{filteredDocs.length === 1 ? '' : 's'}</p>
            {loading && <span className="text-xs text-slate-400">Cargando...</span>}
          </div>
          {filteredDocs.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm"><FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />Sin documentos con estos filtros</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {pagedDocs.map(d => {
                const Icon = iconForCategoria(categorias.find(c => c.id === d.categoria_id)?.icono);
                const badge = estadoBadge(d.estado_vigencia);
                return (
                  <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-3 p-3 hover:bg-slate-50/60 transition-colors"
                    data-testid={`portal-doc-${d.id}`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-4 h-4 text-teal-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {d.codigo && <span className="font-mono text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{d.codigo}</span>}
                        <p className="text-sm font-semibold text-slate-800">{d.nombre}</p>
                        <span className="text-[10px] font-mono bg-slate-100 text-slate-500 px-1 py-0.5 rounded">v{d.version}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full ${d.categoria_tipo === 'transversal' ? 'bg-cyan-50 text-cyan-700' : 'bg-orange-50 text-orange-700'}`}>{d.categoria_nombre}</span>
                        {d.gerencia_id && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><Building2 className="w-2.5 h-2.5" />{gerenciaNombre(d.gerencia_id)}</span>}
                        {d.fecha_vigencia && <span className="text-[10px] text-slate-500">Vence: {fmtDate(d.fecha_vigencia)}</span>}
                        {badge && (
                          <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${badge.cls}`}>
                            <badge.Icon className="w-2.5 h-2.5" /> {badge.label}
                          </span>
                        )}
                      </div>
                      {d.descripcion && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{d.descripcion}</p>}
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 shrink-0 self-center" />
                  </a>
                );
              })}
            </div>
          )}
          {filteredDocs.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs">
              <span className="text-slate-500">Mostrando {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredDocs.length)} de {filteredDocs.length}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">Anterior</button>
                <span className="px-2 font-semibold text-slate-700">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed">Siguiente</button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function MiniCat({ nombre, icon: Icon, color, count, selected, onClick }) {
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 p-2.5 rounded-lg border-2 transition-all text-left ${selected ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200'}`}
      data-testid={`mini-cat-${nombre}`}
    >
      <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-3.5 h-3.5" style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-slate-800 truncate leading-tight">{nombre}</p>
        <p className="text-[10px] text-slate-400 leading-tight">{count}</p>
      </div>
    </button>
  );
}

export default PortalRepositorio;
